/* Utilities for async await syntax */

import { from, isObservable, Observable, of, Subject } from 'rxjs';
import { map, reduce, take, takeUntil, timeoutWith } from 'rxjs/operators';

/**
 * Return a promise for the first item emitted from an Observable.
 *
 * @param obs An observable to watch
 * @returns Promise of emitted value
 *
 * Helpful when using async await style
 *
 * @example
 *
 * async someFn() {
 *   try {
 *     const data = await getFirst(this.data$);
 *     // do something with the data
 *   } catch (error) {
 *     // handle error
 *   }
 * }
 *
 * Primarily for use in unit tests of a service's observable properties
 *
 * // jasmine test with async await
 *
 * // Mock a service dependency
 * class testDependency {
 *   testSource = new Subject();
 *   source$ = testSource.asObservable() // service requires this observable
 * }
 *
 * let dependency: TestDependency;
 * let service: ServiceToTest;
 *
 * beforeEach(() => {
 *   dependency = new TestDependency();
 *
 *   // create service with test dependency that you control
 *   service = new service(dependency as Dependency)
 * })
 *
 * it('should ...', async () => {
 *   const testData = ... // source initial state
 *   const expected = ... // service's emitted state
 *
 *   // emit test value
 *   dependency.testSource.next(testData);
 *
 *   const actual = await getFirst(service.someProp$);
 *   expect(actual).toEqual(expected);
 * });
 */
export function getFirst<T>(obs: Observable<T>): Promise<T> {
  return obs.pipe(take(1)).toPromise();
}

/**
  * Get first emitted value from observable or timeout as a Promise<[emitted-value, boolean]>(for testing)
  * @param obs observable of type T to try
  * @param timeoutMs ms before timeout
  * @param [timeoutResult] optional value to return if observable times out (default: null)
  * @returns Promise resolving to tuple [emitted-value, timeout:boolean]
  *
  * @example
  *
  * // Try to get chat within 30ms
  * const [chat, timeout] = await firstOrTimeout(service.chatIsAvailable$, 30);
  * expect(chat).toBeNull('is null');
  * expect(timeout).toBe(false, 'no timeout');
  */
export function firstOrTimeout<T>(
    obs: Observable<T>,
    timeoutMs: number,
    timeoutResult: T = null
  ): Promise<[T, boolean]> {
  return obs.pipe(
    take(1),
    map(v => [v, false] as [T, boolean]),
    timeoutWith(timeoutMs, of([timeoutResult, true] as [T, boolean])),
  )
  .toPromise();
}

/**
 * Return a promise of array holding all items emitted by a terminating Observable.
 * Completes when the observable terminates.
 *
 *
 * @param obs An observable to watch
 * @param [act] optional function for executing a sequence of test actions.
 * If defined, getAll() calls this function with a notifier subject before returning the promise.
 *
 * @returns Promise array of emitted values
 *
 * Helpful for gathering emitted observable values using async await style
 *
 * @example
 *
 * async someFn() {
 *   try {
 *     // assuming this.data$ completes on its own
 *     const data = await getAll(this.data$);
 *     // do something with the data
 *   } catch (error) {
 *     // handle error
 *   }
 * }
 *
 * Primarily for use in unit tests of a service's observable properties
 * when you want to verify a sequence of observable values from a property
 *
 * // jasmine test with async await
 *
 * // Mock a service dependency
 * class testDependency {
 *   testSource = new Subject();
 *   source$ = testSource.asObservable() // service requires this observable
 * }
 *
 * let dependency: TestDependency;
 * let service: ServiceToTest;
 *
 * beforeEach(() => {
 *   dependency = new TestDependency();
 *
 *   // create service with test dependency that you control
 *   service = new service(dependency as Dependency)
 * })
 *
 * it('should ...', async () => {
 *
 *   // expected values emitted by service property observable
 *   const expected: any[] = [...];
 *
 *   // Test actions as a function with notifier
 *   function act(stop: Subject<any>) {
 *     dependency.testSource.next(...);
 *     dependency.testSource.next(null); // service property won't emit
 *     dependency.testSource.next(...);
 *     stop.next();
 *   }
 *
 *   const actual = await getAll(service.someObservableProperty$, act);
 *   expect(actual.length).toBe(2);
 *   expect(actual[0]).toEqual(expected[0]);
 *   expect(actual[1]).toEqual(expected[1]);
 * );
 *
 * If you need a delay between action steps, consider the following example
 *
 *   const wait = (delayMs = 1) => timer(delayMs).toPromise();
 *
 *   // Test actions with delays between steps
 *   async function act(stop: Subject<any>) {
 *     dependency.testSource.next(...);
 *     await wait();
 *     dependency.testSource.next(null); // service property won't emit
 *     await wait();
 *     dependency.testSource.next(...);
 *     await wait();
 *     stop.next();
 *   }
 */
export function getAll<T>(obs: Observable<T>, act?: (stop?: Subject<any>) => void): Promise<T[]> {
  // notifier observable that can complete the `obs` observable
  const stop = new Subject();

  const promise = obs.pipe(
    takeUntil(stop),
    reduce((acc: T[], current: T) => acc.concat(current), [] as T[]),
  ).toPromise();

  if (act) {
    act(stop);
  }

  return promise;
}

/** Wait specified ms (default: 1ms). Returns promise you can await. For testing */
export function wait(ms = 1) {
  return new Promise<number>(resolve => setTimeout(() => resolve(ms), ms) );
}

/** Return the value in an observable */
export function wrapIntoObservable<T>(value: T | Promise<T> | Observable<T>): Observable<T> {
  if (isObservable(value)) {
    return value;
  }

  if (value instanceof Promise || (value as any).then) {
    return from(Promise.resolve(value));
  }

  return of(value);
}